import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { MACOS_DOWNLOAD_URL, trackDownloadClick } from "@/components/desktop/PricingSection";
import { attributionQueryString } from "@/lib/attribution";
import { marketingConsentDeeplinkParam, marketingConsentGranted } from "@/lib/marketingConsent";
import { trackEvent } from "@/utils/mixpanel";

// vovy.ai/open — the EMAIL-SAFE launcher. Custom-scheme hrefs (vovy://…) get
// stripped or de-linked by Gmail and friends, so emails link HERE and this page
// does the scheme jump. Two outcomes, both first-class:
//   • Vovy installed → the deep link fires and the app comes forward. Signed-in
//     visitors get the SAME single-use auth handoff /welcome mints (the app
//     opens already signed in); signed-out visitors get a plain vovy:// open.
//   • Not installed → the OS ignores the scheme silently, and the Download
//     button is already on screen — no error, no dead end.
// Deliberately a sibling of Welcome, not a mode of it: /welcome is the
// post-PURCHASE moment with plan copy and celebration; this is a utilitarian
// "get me back into the app" hop that any email can safely target.
// Attribution params (utm_*/gclid/fbclid) ride the deep link so the desktop
// app can adopt the web session's ad attribution.
const plainOpen = () => {
  const attr = attributionQueryString();
  const consent = marketingConsentDeeplinkParam();
  return `vovy://open?${consent}${attr ? `&${attr}` : ""}`;
};

async function mintDeeplink(): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke("ext-session-mint", {
      body: { marketing_consent: marketingConsentGranted() ? "granted" : "denied" }
    });
    if (error || !data?.handoff_token) return null;
    const attr = attributionQueryString();
    const consent = marketingConsentDeeplinkParam();
    return `vovy://auth/handoff?token=${data.handoff_token as string}&${consent}${attr ? `&${attr}` : ""}`;
  } catch {
    return null;
  }
}

const Open = () => {
  const { user, loading } = useAuth();
  const [opening, setOpening] = useState(true);
  const autoAttempted = useRef(false);

  // Funnel: the launcher page rendered (email click landed).
  useEffect(() => {
    trackEvent("open_launcher_viewed");
  }, []);

  useEffect(() => {
    if (loading) return;
    if (autoAttempted.current) return;
    autoAttempted.current = true;
    let cancelled = false;
    (async () => {
      // Signed-in → auth handoff (single-use token, app opens signed in).
      // Signed-out or mint failure → plain scheme open; the app handles it.
      const link = (user && (await mintDeeplink())) || plainOpen();
      if (cancelled) return;
      setTimeout(() => {
        trackEvent("open_deeplink_fired", {
          kind: link.startsWith("vovy://auth/handoff") ? "handoff" : "plain",
          auto: true,
        });
        window.location.href = link;
        // The scheme jump either worked (app foregrounded) or silently didn't
        // (not installed). Either way stop implying work is happening.
        setTimeout(() => setOpening(false), 1500);
      }, 400);
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, user]);

  const openNow = async () => {
    setOpening(true);
    const link = (user && (await mintDeeplink())) || plainOpen();
    trackEvent("open_deeplink_fired", {
      kind: link.startsWith("vovy://auth/handoff") ? "handoff" : "plain",
      auto: false,
    });
    window.location.href = link;
    setTimeout(() => setOpening(false), 1500);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
      <SEOHead
        title="Opening Vovy…"
        description="Opens the Vovy app on your Mac, or grabs you the download if it isn't installed yet."
        noindex
      />
      <h1 className="text-3xl font-bold mb-3">Opening Vovy…</h1>
      <p className="text-muted-foreground mb-8 max-w-md">
        If nothing happened, Vovy isn&apos;t installed on this Mac yet — grab it below.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={openNow}
          disabled={opening}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60"
        >
          {opening ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Open Vovy
        </button>
        <a
          href={MACOS_DOWNLOAD_URL}
          onClick={(e) => trackDownloadClick("open_page", e)}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3 font-semibold"
        >
          <Download className="h-4 w-4" />
          Download for Mac
        </a>
      </div>
      <p className="mt-8 text-sm text-muted-foreground">
        New here? <Link to="/" className="underline">See what Vovy does</Link>
      </p>
    </div>
  );
};

export default Open;
